import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Button,
  Chip,
  IconButton,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Card,
  useTheme,
  alpha,
  Grid,
  Tabs,
  Tab,
  CircularProgress,
} from '@mui/material';
import AuthenticatedAvatar from '../../../components/AuthenticatedAvatar';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  PhotoCamera as PhotoCameraIcon,
  Upload as UploadIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Close as CloseIcon,
  CameraAlt as CameraAltIcon,
  People as PeopleIcon,
  Female as FemaleIcon,
  Male as MaleIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef, GridToolbar } from '@mui/x-data-grid';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'https://stardust-aims.amy.care';

interface ProfilePhoto {
  url: string;
  is_default: boolean;
  uploaded_at: string;
}

interface Province {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  is_active: boolean;
}

interface District {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  province_code: number;
  is_active: boolean;
}

interface SubDistrict {
  _id: string;
  code: number;
  name: Array<{ code: string; name: string }>;
  en_name: string;
  district_code: number;
  province_code: number;
  zipcode?: string;
  is_active: boolean;
}

interface Degree {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  level: number;
  is_active: boolean;
}

interface Faculty {
  _id: string;
  code: string;
  name_th: string;
  name_en: string;
  abbreviation_th?: string;
  abbreviation_en?: string;
  is_active: boolean;
}

interface Alumni {
  _id: string;
  student_id: string;
  citizen_id: string;
  title: string;
  nickname?: string;
  first_name_th: string;
  last_name_th: string;
  first_name_en: string;
  last_name_en: string;
  current_first_name_th?: string;
  current_last_name_th?: string;
  current_first_name_en?: string;
  current_last_name_en?: string;
  birth_date: string;
  gender: string;
  professional_license_number?: string;
  alumni_type: string;
  batch?: string;
  ideals?: string[];
  approval_status: string;
  profile_photos?: ProfilePhoto[];
  education: {
    degree: string;
    faculty: string;
    graduation_year: number;
    gpa?: number;
  };
  contact: {
    email: string;
    phone: string;
    line_id?: string;
    facebook?: string;
  };
  address: {
    address_line: string;
    sub_district_code: number;
    sub_district: string;
    district_code: number;
    district: string;
    province_code: number;
    province: string;
    postal_code: string;
    country: string;
  };
  work_address?: {
    address_line: string;
    sub_district_code?: number;
    sub_district: string;
    district_code?: number;
    district: string;
    province_code?: number;
    province: string;
    postal_code: string;
    country: string;
  };
  current_occupation?: string;
  user_id: string;
}

// Statistics interface
interface AlumniStatistics {
  total: number;
  byGender: { [key: string]: number };
  byBatch: { [key: string]: number };
}

function AlumniManagement() {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [alumni, setAlumni] = useState<Alumni[]>([]);
  const [filteredAlumni, setFilteredAlumni] = useState<Alumni[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state for server-side pagination
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 50,
  });
  const [totalRows, setTotalRows] = useState(0);
  
  // Statistics state
  const [statistics, setStatistics] = useState<AlumniStatistics>({
    total: 0,
    byGender: {},
    byBatch: {},
  });
  const [statsLoading, setStatsLoading] = useState(true);
  
  // Filter states
  const [filters, setFilters] = useState({
    approvalStatus: 'all',
    alumniType: 'all',
    graduationYear: 'all',
    searchText: '',
  });
  
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedAlumni, setSelectedAlumni] = useState<Alumni | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Photo management state
  const [profilePhotos, setProfilePhotos] = useState<ProfilePhoto[]>([]);
  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [cameraActive, setCameraActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [photoError, setPhotoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Legacy data update state
  const [updatingLegacyData, setUpdatingLegacyData] = useState(false);
  const [cleaningOrphanedUsers, setCleaningOrphanedUsers] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  
  // Geolocation state
  const [provinces, setProvinces] = useState<Province[]>([]);
  const [homeDistricts, setHomeDistricts] = useState<District[]>([]);
  const [homeSubDistricts, setHomeSubDistricts] = useState<SubDistrict[]>([]);
  const [workDistricts, setWorkDistricts] = useState<District[]>([]);
  const [workSubDistricts, setWorkSubDistricts] = useState<SubDistrict[]>([]);
  const [geoLoading, setGeoLoading] = useState(false);
  
  // Education master data state
  const [degrees, setDegrees] = useState<Degree[]>([]);
  const [faculties, setFaculties] = useState<Faculty[]>([]);
  
  const [formData, setFormData] = useState({
    student_id: '',
    citizen_id: '',
    title: 'นาย',
    nickname: '',
    first_name_th: '',
    last_name_th: '',
    first_name_en: '',
    last_name_en: '',
    current_first_name_th: '',
    current_last_name_th: '',
    current_first_name_en: '',
    current_last_name_en: '',
    birth_date: '',
    gender: 'male',
    professional_license_number: '',
    alumni_type: 'regular',
    batch: '',
    ideals: '' as string,
    degree: '',
    faculty: '',
    graduation_year: new Date().getFullYear(),
    gpa: '',
    email: '',
    phone: '',
    line_id: '',
    facebook: '',
    address_line: '',
    province_code: '',
    district_code: '',
    sub_district_code: '',
    sub_district: '',
    district: '',
    province: '',
    postal_code: '',
    country: 'Thailand',
    work_address_line: '',
    work_province_code: '',
    work_district_code: '',
    work_sub_district_code: '',
    work_sub_district: '',
    work_district: '',
    work_province: '',
    work_postal_code: '',
    work_country: 'Thailand',
    current_occupation: '',
  });

  useEffect(() => {
    fetchProvinces();
    fetchDegrees();
    fetchFaculties();
    fetchStatistics();
  }, []);

  // Fetch alumni when pagination or filters change
  useEffect(() => {
    fetchAlumni();
  }, [paginationModel, filters]);

  // Fetch statistics
  const fetchStatistics = async () => {
    try {
      setStatsLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/alumni/public/statistics`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const data = response.data;
      setStatistics({
        total: data.total_alumni || 0,
        byGender: data.by_gender || {},
        byBatch: data.by_graduation_year || {},
      });
    } catch (err) {
      console.error('Failed to fetch statistics:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchAlumni = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Build params for server-side pagination and filtering
      const params: any = { 
        skip: paginationModel.page * paginationModel.pageSize,
        limit: paginationModel.pageSize,
      };
      
      // Add filter params for server-side filtering
      if (filters.approvalStatus !== 'all') {
        params.approval_status = filters.approvalStatus;
      }
      if (filters.searchText.trim()) {
        params.search = filters.searchText.trim();
      }

      const response = await axios.get(`${API_URL}/api/v1/alumni`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const alumniData = response.data.data || [];
      const total = response.data.total || alumniData.length;
      
      setAlumni(alumniData);
      setTotalRows(total);
      
      // Apply client-side filters for fields not supported by server
      applyFilters(alumniData);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch alumni');
    } finally {
      setLoading(false);
    }
  };

  // Apply client-side filters
  const applyFilters = (alumniData: Alumni[] = alumni) => {
    let filtered = [...alumniData];

    // Approval status filter
    if (filters.approvalStatus !== 'all') {
      filtered = filtered.filter(a => a.approval_status === filters.approvalStatus);
    }

    // Alumni type filter
    if (filters.alumniType !== 'all') {
      filtered = filtered.filter(a => a.alumni_type === filters.alumniType);
    }

    // Graduation year filter
    if (filters.graduationYear !== 'all') {
      filtered = filtered.filter(a => a.education.graduation_year.toString() === filters.graduationYear);
    }

    // Search text filter (name, student ID, email)
    if (filters.searchText.trim()) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(a => 
        a.first_name_th.toLowerCase().includes(searchLower) ||
        a.last_name_th.toLowerCase().includes(searchLower) ||
        a.first_name_en.toLowerCase().includes(searchLower) ||
        a.last_name_en.toLowerCase().includes(searchLower) ||
        a.student_id.toLowerCase().includes(searchLower) ||
        a.contact.email.toLowerCase().includes(searchLower) ||
        (a.nickname && a.nickname.toLowerCase().includes(searchLower))
      );
    }

    setFilteredAlumni(filtered);
  };

  // Handle filter changes - reset to page 0 when filters change
  const handleFilterChange = (filterName: string, value: string) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);
    // Reset to first page when filters change
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  };

  // Geolocation helper functions
  const getThaiName = (item: Province | District | SubDistrict) => {
    return item.name?.find((n) => n.code === 'th')?.name || item.en_name;
  };

  const fetchProvinces = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/provinces`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProvinces(response.data);
    } catch (err) {
      console.error('Failed to fetch provinces:', err);
    }
  };

  const fetchDistricts = async (provinceCode: number, isWorkAddress: boolean = false) => {
    try {
      setGeoLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/districts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { province_code: provinceCode }
      });
      if (isWorkAddress) {
        setWorkDistricts(response.data);
        setWorkSubDistricts([]);
      } else {
        setHomeDistricts(response.data);
        setHomeSubDistricts([]);
      }
    } catch (err) {
      console.error('Failed to fetch districts:', err);
    } finally {
      setGeoLoading(false);
    }
  };

  const fetchSubDistricts = async (districtCode: number, isWorkAddress: boolean = false) => {
    try {
      setGeoLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/sub-districts`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { district_code: districtCode, limit: 5000 }
      });
      const items = response.data.items || response.data;
      if (isWorkAddress) {
        setWorkSubDistricts(items);
      } else {
        setHomeSubDistricts(items);
      }
    } catch (err) {
      console.error('Failed to fetch sub-districts:', err);
    } finally {
      setGeoLoading(false);
    }
  };

  // Education master data fetch functions
  const fetchDegrees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/degrees`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { is_active: true }
      });
      setDegrees(response.data);
    } catch (err) {
      console.error('Failed to fetch degrees:', err);
    }
  };

  const fetchFaculties = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/v1/master-data/faculties`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { is_active: true }
      });
      setFaculties(response.data);
    } catch (err) {
      console.error('Failed to fetch faculties:', err);
    }
  };

  const handleCreate = () => {
    setSelectedAlumni(null);
    setProfilePhotos([]);
    setFormData({
      student_id: '',
      citizen_id: '',
      title: 'นาย',
      nickname: '',
      first_name_th: '',
      last_name_th: '',
      first_name_en: '',
      last_name_en: '',
      current_first_name_th: '',
      current_last_name_th: '',
      current_first_name_en: '',
      current_last_name_en: '',
      birth_date: '',
      gender: 'male',
      professional_license_number: '',
      alumni_type: 'regular',
      batch: '',
      ideals: '',
      degree: '',
      faculty: '',
      graduation_year: new Date().getFullYear(),
      gpa: '',
      email: '',
      phone: '',
      line_id: '',
      facebook: '',
      address_line: '',
      province_code: '',
      district_code: '',
      sub_district_code: '',
      sub_district: '',
      district: '',
      province: '',
      postal_code: '',
      country: 'Thailand',
      work_address_line: '',
      work_province_code: '',
      work_district_code: '',
      work_sub_district_code: '',
      work_sub_district: '',
      work_district: '',
      work_province: '',
      work_postal_code: '',
      work_country: 'Thailand',
      current_occupation: '',
    });
    setHomeDistricts([]);
    setHomeSubDistricts([]);
    setWorkDistricts([]);
    setWorkSubDistricts([]);
    setTabValue(0);
    setEditDialogOpen(true);
  };

  const handleEdit = (alumniData: Alumni) => {
    navigate(`/alumni/edit/${alumniData._id}`);
  };

  // Photo Management Functions
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setPhotoError('Please select an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      setPhotoError('File size must be less than 5MB');
      return;
    }

    await uploadPhoto(file);
  };

  const uploadPhoto = async (file: File) => {
    setUploading(true);
    setPhotoError(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'profiles');

      const response = await axios.post(`${API_URL}/api/v1/upload/`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      const newPhoto: ProfilePhoto = {
        url: response.data.url,
        is_default: profilePhotos.length === 0, // First photo is default
        uploaded_at: new Date().toISOString(),
      };

      setProfilePhotos([...profilePhotos, newPhoto]);
      setPhotoDialogOpen(false);
    } catch (err: any) {
      setPhotoError(err.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const handleStartCamera = async () => {
    setCameraActive(true);
    setPhotoError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' },
        audio: false,
      });
      
      setVideoStream(stream);
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current?.play();
        };
      }
    } catch (err) {
      setPhotoError('Failed to access camera: ' + (err as Error).message);
      setCameraActive(false);
    }
  };

  const handleStopCamera = () => {
    if (videoStream) {
      videoStream.getTracks().forEach(track => track.stop());
      setVideoStream(null);
    }
    setCameraActive(false);
  };

  const handleCapturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0);
      
      canvas.toBlob(async (blob) => {
        if (blob) {
          const file = new File([blob], 'photo.jpg', { type: 'image/jpeg' });
          await uploadPhoto(file);
          handleStopCamera();
        }
      }, 'image/jpeg', 0.9);
    }
  };

  const handleSetDefaultPhoto = (index: number) => {
    const updated = profilePhotos.map((photo, i) => ({
      ...photo,
      is_default: i === index,
    }));
    setProfilePhotos(updated);
  };

  const handleRemovePhoto = (index: number) => {
    const photo = profilePhotos[index];
    const updated = profilePhotos.filter((_, i) => i !== index);
    
    // If removed photo was default and there are other photos, set first as default
    if (photo.is_default && updated.length > 0) {
      updated[0].is_default = true;
    }
    
    setProfilePhotos(updated);
  };

  // Cleanup camera stream on unmount
  useEffect(() => {
    return () => {
      if (videoStream) {
        videoStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [videoStream]);

  const handleSave = async () => {
    try {
      const token = localStorage.getItem('token');
      
      const idealsArray = formData.ideals 
        ? formData.ideals.split(',').map(i => i.trim()).filter(i => i.length > 0)
        : [];

      // Find selected locations for home address
      const selectedProvince = provinces.find(p => p.code === parseInt(formData.province_code));
      const selectedDistrict = homeDistricts.find(d => d.code === parseInt(formData.district_code));
      const selectedSubDistrict = homeSubDistricts.find(s => s.code === parseInt(formData.sub_district_code));

      const workAddressData = formData.work_address_line && formData.work_province_code ? (() => {
        const workProvince = provinces.find(p => p.code === parseInt(formData.work_province_code));
        const workDistrict = workDistricts.find(d => d.code === parseInt(formData.work_district_code));
        const workSubDistrict = workSubDistricts.find(s => s.code === parseInt(formData.work_sub_district_code));
        return {
          address_line: formData.work_address_line,
          province_code: parseInt(formData.work_province_code),
          province: workProvince ? getThaiName(workProvince) : formData.work_province,
          district_code: parseInt(formData.work_district_code),
          district: workDistrict ? getThaiName(workDistrict) : formData.work_district,
          sub_district_code: parseInt(formData.work_sub_district_code),
          sub_district: workSubDistrict ? getThaiName(workSubDistrict) : formData.work_sub_district,
          postal_code: formData.work_postal_code,
          country: formData.work_country,
        };
      })() : null;

      const data = {
        student_id: formData.student_id,
        citizen_id: formData.citizen_id,
        title: formData.title,
        nickname: formData.nickname || null,
        first_name_th: formData.first_name_th,
        last_name_th: formData.last_name_th,
        first_name_en: formData.first_name_en,
        last_name_en: formData.last_name_en,
        current_first_name_th: formData.current_first_name_th || null,
        current_last_name_th: formData.current_last_name_th || null,
        current_first_name_en: formData.current_first_name_en || null,
        current_last_name_en: formData.current_last_name_en || null,
        birth_date: formData.birth_date,
        gender: formData.gender,
        professional_license_number: formData.professional_license_number || null,
        alumni_type: formData.alumni_type,
        batch: formData.batch || null,
        ideals: idealsArray,
        education: {
          degree: formData.degree,
          faculty: formData.faculty,
          graduation_year: formData.graduation_year,
          gpa: formData.gpa ? parseFloat(formData.gpa) : null,
        },
        contact: {
          email: formData.email,
          phone: formData.phone,
          line_id: formData.line_id || null,
          facebook: formData.facebook || null,
        },
        address: {
          address_line: formData.address_line,
          province_code: parseInt(formData.province_code),
          province: selectedProvince ? getThaiName(selectedProvince) : formData.province,
          district_code: parseInt(formData.district_code),
          district: selectedDistrict ? getThaiName(selectedDistrict) : formData.district,
          sub_district_code: parseInt(formData.sub_district_code),
          sub_district: selectedSubDistrict ? getThaiName(selectedSubDistrict) : formData.sub_district,
          postal_code: formData.postal_code,
          country: formData.country,
        },
        work_address: workAddressData,
        additional_education: [],
        trainings: [],
        work_history: [],
        current_occupation: formData.current_occupation || null,
        profile_photos: profilePhotos,
      };

      // Validate at least one default photo if photos are provided
      if (profilePhotos.length > 0 && !profilePhotos.some(p => p.is_default)) {
        setError('Please select a default profile photo');
        return;
      }

      if (selectedAlumni) {
        await axios.put(`${API_URL}/api/v1/alumni/${selectedAlumni._id}`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_URL}/api/v1/alumni`, data, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }

      setEditDialogOpen(false);
      setProfilePhotos([]);
      fetchAlumni();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save alumni');
    }
  };

  const handleDelete = (alumniData: Alumni) => {
    setSelectedAlumni(alumniData);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedAlumni) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/v1/alumni/${selectedAlumni._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setDeleteDialogOpen(false);
      setSelectedAlumni(null);
      fetchAlumni();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete alumni');
    }
  };

  const handleApproval = async (alumniData: Alumni, action: 'approve' | 'reject') => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/v1/alumni/${alumniData._id}/approval`,
        { action },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchAlumni();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update approval status');
    }
  };

  const handleUpdateLegacyData = async () => {
    setUpdatingLegacyData(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/alumni/update-legacy-degrees`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      alert(`✅ Success!\n\n${data.message}\n\nUpdated ${data.updated_count} alumni records with legacy data.`);
      fetchAlumni(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to update legacy data');
    } finally {
      setUpdatingLegacyData(false);
    }
  };

  const handleCleanupOrphanedUsers = async () => {
    if (!window.confirm('Are you sure you want to clean up orphaned user references? This will remove user_id links that point to non-existent users.')) {
      return;
    }
    
    setCleaningOrphanedUsers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/v1/alumni/cleanup-orphaned-users`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      alert(`✅ Cleanup Complete!\n\n${data.message}\n\nCleaned: ${data.cleaned_count}\nVerified: ${data.verified_count}\nTotal Processed: ${data.total_processed}`);
      fetchAlumni(); // Refresh the list
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to cleanup orphaned users');
    } finally {
      setCleaningOrphanedUsers(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      default: return 'default';
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'student_id',
      headerName: t('alumni.columns.studentId'),
      width: 120,
    },
    {
      field: 'name_th',
      headerName: t('alumni.columns.nameTh'),
      width: 200,
      valueGetter: (_value: any, row: any) => `${row.first_name_th} ${row.last_name_th}`,
    },
    {
      field: 'name_en',
      headerName: t('alumni.columns.nameEn'),
      width: 200,
      valueGetter: (_value: any, row: any) => `${row.first_name_en} ${row.last_name_en}`,
    },
    {
      field: 'alumni_type',
      headerName: t('alumni.columns.type'),
      width: 120,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'degree',
      headerName: t('alumni.columns.degree'),
      width: 150,
      valueGetter: (_value: any, row: any) => row.education?.degree || '-',
    },
    {
      field: 'faculty',
      headerName: t('alumni.columns.faculty'),
      width: 150,
      valueGetter: (_value: any, row: any) => row.education?.faculty || '-',
    },
    {
      field: 'graduation_year',
      headerName: t('alumni.columns.year'),
      width: 100,
      valueGetter: (_value: any, row: any) => row.education?.graduation_year || '-',
    },
    {
      field: 'approval_status',
      headerName: t('alumni.columns.status'),
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          color={getStatusColor(params.value) as any}
          size="small"
          sx={{ textTransform: 'capitalize' }}
        />
      ),
    },
    {
      field: 'updated_at',
      headerName: t('alumni.columns.updatedAt'),
      width: 160,
      valueGetter: (_value: any, row: any) => row.updated_at ? new Date(row.updated_at).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-',
    },
    {
      field: 'actions',
      headerName: t('alumni.columns.actions'),
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            onClick={() => navigate(`/alumni/${params.row._id}`)}
            sx={{ color: theme.palette.info.main }}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{ color: theme.palette.primary.main }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          {params.row.approval_status === 'pending' && (
            <>
              <IconButton
                size="small"
                onClick={() => handleApproval(params.row, 'approve')}
                sx={{ color: theme.palette.success.main }}
              >
                <ApproveIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleApproval(params.row, 'reject')}
                sx={{ color: theme.palette.error.main }}
              >
                <RejectIcon fontSize="small" />
              </IconButton>
            </>
          )}
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            sx={{ color: theme.palette.error.main }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ),
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Stack spacing={3}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={600}>
              {t('alumni.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {t('alumni.subtitle')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={updatingLegacyData ? <CircularProgress size={16} /> : <UploadIcon />}
              onClick={handleUpdateLegacyData}
              disabled={updatingLegacyData}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {updatingLegacyData ? 'Updating...' : 'Update Legacy Data'}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={cleaningOrphanedUsers ? <CircularProgress size={16} /> : <DeleteIcon />}
              onClick={handleCleanupOrphanedUsers}
              disabled={cleaningOrphanedUsers}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {cleaningOrphanedUsers ? 'Cleaning...' : 'Cleanup Orphaned Users'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreate}
            >
              {t('alumni.add')}
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={2}>
          {/* Total Alumni Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 2, 
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 100%)`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha(theme.palette.primary.main, 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} color="primary.main">
                    {statsLoading ? <CircularProgress size={24} /> : statistics.total.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.statistics.total', 'Total Alumni')}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          {/* Female Count Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 2, 
                background: `linear-gradient(135deg, ${alpha('#E91E63', 0.1)} 0%, ${alpha('#E91E63', 0.05)} 100%)`,
                border: `1px solid ${alpha('#E91E63', 0.2)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha('#E91E63', 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <FemaleIcon sx={{ fontSize: 32, color: '#E91E63' }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#E91E63' }}>
                    {statsLoading ? <CircularProgress size={24} /> : (statistics.byGender['female'] || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.statistics.female', 'Female')}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

          {/* Male Count Card */}
          <Grid item xs={12} sm={6} md={3}>
            <Card 
              sx={{ 
                p: 2, 
                background: `linear-gradient(135deg, ${alpha('#2196F3', 0.1)} 0%, ${alpha('#2196F3', 0.05)} 100%)`,
                border: `1px solid ${alpha('#2196F3', 0.2)}`,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2}>
                <Box 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    bgcolor: alpha('#2196F3', 0.15),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <MaleIcon sx={{ fontSize: 32, color: '#2196F3' }} />
                </Box>
                <Box>
                  <Typography variant="h4" fontWeight={700} sx={{ color: '#2196F3' }}>
                    {statsLoading ? <CircularProgress size={24} /> : (statistics.byGender['male'] || 0).toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('alumni.statistics.male', 'Male')}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>

        </Grid>

        <Card sx={{ border: `1px solid ${theme.palette.divider}`, boxShadow: theme.shadows[1] }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
            <Grid container spacing={2} alignItems="center">
              {/* Search Field */}
              <Grid item xs={12} md={3}>
                <TextField
                  size="small"
                  fullWidth
                  label={t('alumni.filters.search')}
                  placeholder={t('alumni.filters.searchPlaceholder')}
                  value={filters.searchText}
                  onChange={(e) => handleFilterChange('searchText', e.target.value)}
                />
              </Grid>

              {/* Approval Status Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t('alumni.filters.approvalStatus')}
                  value={filters.approvalStatus}
                  onChange={(e) => handleFilterChange('approvalStatus', e.target.value)}
                >
                  <MenuItem value="all">{t('alumni.filters.allStatus')}</MenuItem>
                  <MenuItem value="pending">{t('common.pending')}</MenuItem>
                  <MenuItem value="approved">{t('common.approved')}</MenuItem>
                  <MenuItem value="rejected">{t('common.rejected')}</MenuItem>
                </TextField>
              </Grid>

              {/* Alumni Type Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t('alumni.filters.alumniType')}
                  value={filters.alumniType}
                  onChange={(e) => handleFilterChange('alumniType', e.target.value)}
                >
                  <MenuItem value="all">{t('alumni.filters.allTypes')}</MenuItem>
                  <MenuItem value="regular">{t('alumni.type.regular')}</MenuItem>
                  <MenuItem value="honorary">{t('alumni.type.honorary')}</MenuItem>
                  <MenuItem value="distinguished">{t('alumni.type.distinguished')}</MenuItem>
                </TextField>
              </Grid>

              {/* Graduation Year Filter */}
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  size="small"
                  fullWidth
                  label={t('alumni.filters.graduationYear')}
                  value={filters.graduationYear}
                  onChange={(e) => handleFilterChange('graduationYear', e.target.value)}
                >
                  <MenuItem value="all">{t('alumni.filters.allYears')}</MenuItem>
                  {Array.from(new Set(alumni.map(a => a.education.graduation_year)))
                    .sort((a, b) => b - a)
                    .map(year => (
                      <MenuItem key={year} value={year.toString()}>{year}</MenuItem>
                    ))}
                </TextField>
              </Grid>
            </Grid>

            {/* Filter Summary */}
            {(filters.approvalStatus !== 'all' || filters.alumniType !== 'all' || 
              filters.graduationYear !== 'all' || filters.searchText) && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('alumni.filters.filtersLabel')}
                </Typography>
                {filters.approvalStatus !== 'all' && (
                  <Chip
                    label={`Status: ${filters.approvalStatus}`}
                    size="small"
                    onDelete={() => handleFilterChange('approvalStatus', 'all')}
                  />
                )}
                {filters.alumniType !== 'all' && (
                  <Chip
                    label={`Type: ${filters.alumniType}`}
                    size="small"
                    onDelete={() => handleFilterChange('alumniType', 'all')}
                  />
                )}
                {filters.graduationYear !== 'all' && (
                  <Chip
                    label={`Year: ${filters.graduationYear}`}
                    size="small"
                    onDelete={() => handleFilterChange('graduationYear', 'all')}
                  />
                )}
                {filters.searchText && (
                  <Chip
                    label={`Search: "${filters.searchText}"`}
                    size="small"
                    onDelete={() => handleFilterChange('searchText', '')}
                  />
                )}
                <Button
                  size="small"
                  onClick={() => setFilters({
                    approvalStatus: 'all',
                    alumniType: 'all',
                    graduationYear: 'all',
                    searchText: '',
                  })}
                >
                  Clear All
                </Button>
              </Box>
            )}

            {/* Result Count */}
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Showing {filteredAlumni.length} of {totalRows.toLocaleString()} alumni (Page {paginationModel.page + 1})
            </Typography>
          </Box>

          <DataGrid
            rows={filteredAlumni}
            columns={columns}
            loading={loading}
            getRowId={(row) => row._id}
            autoHeight
            pageSizeOptions={[25, 50, 100]}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            paginationMode="server"
            rowCount={totalRows}
            slots={{ toolbar: GridToolbar }}
            slotProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            sx={{
              border: 'none',
              '& .MuiDataGrid-cell:focus': { outline: 'none' },
              '& .MuiDataGrid-row:hover': {
                bgcolor: alpha(theme.palette.primary.main, 0.04),
              },
            }}
          />
        </Card>
      </Stack>

      {/* Create/Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {selectedAlumni ? t('alumni.edit') : t('alumni.add')}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Tabs value={tabValue} onChange={(_, val) => setTabValue(val)}>
              <Tab label={t('alumni.tabs.profilePhotos')} />
              <Tab label={t('alumni.tabs.personalInfo')} />
              <Tab label={t('alumni.tabs.education')} />
              <Tab label={t('alumni.tabs.contactAddress')} />
            </Tabs>
          </Box>

          {/* Tab 0: Profile Photos */}
          {tabValue === 0 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {t('alumni.tabs.profilePhotos')}
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  {t('common.uploadPhoto')} ({t('common.optional')}). {t('alumni.labels.setAsDefault')}.
                </Alert>
                
                {photoError && (
                  <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPhotoError(null)}>
                    {photoError}
                  </Alert>
                )}

                {/* Photo Grid */}
                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {profilePhotos.map((photo, index) => (
                    <Grid item xs={4} key={index}>
                      <Card sx={{ position: 'relative' }}>
                        <AuthenticatedAvatar
                          src={photo.url}
                          sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 1,
                            border: photo.is_default ? `3px solid ${theme.palette.primary.main}` : 'none',
                          }}
                        />
                        <Box sx={{ position: 'absolute', top: 8, right: 8, display: 'flex', gap: 0.5 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleSetDefaultPhoto(index)}
                            sx={{
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: 'background.paper' },
                            }}
                          >
                            {photo.is_default ? (
                              <StarIcon sx={{ color: 'gold' }} />
                            ) : (
                              <StarBorderIcon />
                            )}
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleRemovePhoto(index)}
                            sx={{
                              bgcolor: 'background.paper',
                              '&:hover': { bgcolor: 'error.main', color: 'white' },
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>
                      </Card>
                    </Grid>
                  ))}

                  {/* Add Photo Button */}
                  {profilePhotos.length < 10 && (
                    <Grid item xs={4}>
                      <Card
                        sx={{
                          height: 200,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          border: `2px dashed ${theme.palette.divider}`,
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                          },
                        }}
                        onClick={() => setPhotoDialogOpen(true)}
                      >
                        <AddIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                          {t('common.add')}
                        </Typography>
                      </Card>
                    </Grid>
                  )}
                </Grid>

                <Typography variant="caption" color="text.secondary">
                  {t('alumni.labels.photoCount', { count: profilePhotos.length })}
                </Typography>
              </Grid>
            </Grid>
          )}

          {/* Tab 1: Personal Info */}
          {tabValue === 1 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {/* ID Section */}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('alumni.sections.identification')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.studentId')} *`}
                  fullWidth
                  required
                  value={formData.student_id}
                  onChange={(e) => setFormData({ ...formData, student_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.labels.citizenId')} *`}
                  fullWidth
                  required
                  value={formData.citizen_id}
                  onChange={(e) => setFormData({ ...formData, citizen_id: e.target.value })}
                />
              </Grid>

              {/* Name Section - Thai */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('alumni.sections.thaiName')}
                </Typography>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  select
                  label={`${t('alumni.labels.title')} *`}
                  fullWidth
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                >
                  <MenuItem value="นาย">{t('alumni.titles.mr')}</MenuItem>
                  <MenuItem value="นาง">{t('alumni.titles.mrs')}</MenuItem>
                  <MenuItem value="นางสาว">{t('alumni.titles.miss')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={2.5}>
                <TextField
                  label={t('alumni.labels.nickname')}
                  fullWidth
                  value={formData.nickname}
                  onChange={(e) => setFormData({ ...formData, nickname: e.target.value })}
                />
              </Grid>
              <Grid item xs={3.75}>
                <TextField
                  label={`${t('alumni.fields.firstNameTh')} *`}
                  fullWidth
                  required
                  value={formData.first_name_th}
                  onChange={(e) => setFormData({ ...formData, first_name_th: e.target.value })}
                />
              </Grid>
              <Grid item xs={3.75}>
                <TextField
                  label={`${t('alumni.fields.lastNameTh')} *`}
                  fullWidth
                  required
                  value={formData.last_name_th}
                  onChange={(e) => setFormData({ ...formData, last_name_th: e.target.value })}
                />
              </Grid>

              {/* Name Section - English */}
              <Grid item xs={12} sx={{ mt: 1 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('alumni.sections.englishName')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.firstNameEn')} *`}
                  fullWidth
                  required
                  value={formData.first_name_en}
                  onChange={(e) => setFormData({ ...formData, first_name_en: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.lastNameEn')} *`}
                  fullWidth
                  required
                  value={formData.last_name_en}
                  onChange={(e) => setFormData({ ...formData, last_name_en: e.target.value })}
                />
              </Grid>

              {/* Current Name Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('alumni.sections.currentName')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.currentFirstNameTh')}
                  fullWidth
                  value={formData.current_first_name_th}
                  onChange={(e) => setFormData({ ...formData, current_first_name_th: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.currentLastNameTh')}
                  fullWidth
                  value={formData.current_last_name_th}
                  onChange={(e) => setFormData({ ...formData, current_last_name_th: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.currentFirstNameEn')}
                  fullWidth
                  value={formData.current_first_name_en}
                  onChange={(e) => setFormData({ ...formData, current_first_name_en: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.currentLastNameEn')}
                  fullWidth
                  value={formData.current_last_name_en}
                  onChange={(e) => setFormData({ ...formData, current_last_name_en: e.target.value })}
                />
              </Grid>

              {/* Personal Details Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('alumni.sections.personalDetails')}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={`${t('alumni.fields.birthDate')} *`}
                  type="date"
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formData.birth_date}
                  onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                />
              </Grid>
              <Grid item xs={4}>
                <TextField
                  select
                  label={`${t('alumni.fields.gender')} *`}
                  fullWidth
                  required
                  value={formData.gender}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                >
                  <MenuItem value="male">{t('alumni.gender.male')}</MenuItem>
                  <MenuItem value="female">{t('alumni.gender.female')}</MenuItem>
                  <MenuItem value="other">{t('alumni.gender.other')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={4}>
                <TextField
                  label={t('alumni.fields.batch')}
                  fullWidth
                  value={formData.batch}
                  onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                />
              </Grid>

              {/* Professional Information Section */}
              <Grid item xs={12} sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('alumni.sections.professionalInfo')}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label={`${t('alumni.fields.alumniType')} *`}
                  fullWidth
                  required
                  value={formData.alumni_type}
                  onChange={(e) => setFormData({ ...formData, alumni_type: e.target.value })}
                >
                  <MenuItem value="regular">{t('alumni.type.regular')}</MenuItem>
                  <MenuItem value="outstanding">{t('alumni.type.distinguished')}</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.professionalLicenseNumber')}
                  fullWidth
                  value={formData.professional_license_number}
                  onChange={(e) => setFormData({ ...formData, professional_license_number: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('alumni.fields.ideals')}
                  fullWidth
                  multiline
                  rows={2}
                  value={formData.ideals}
                  onChange={(e) => setFormData({ ...formData, ideals: e.target.value })}
                  helperText={t('alumni.fields.idealsHelper')}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 2: Education */}
          {tabValue === 2 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField
                  select
                  label={`${t('alumni.fields.degree')} *`}
                  fullWidth
                  required
                  value={formData.degree}
                  onChange={(e) => {
                    setFormData({ ...formData, degree: e.target.value });
                  }}
                >
                  {degrees.map((degree) => (
                    <MenuItem key={degree.code} value={degree.name_en}>
                      {degree.name_en} ({degree.name_th})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label={`${t('alumni.fields.faculty')} *`}
                  fullWidth
                  required
                  value={formData.faculty}
                  onChange={(e) => {
                    setFormData({ ...formData, faculty: e.target.value });
                  }}
                >
                  {faculties.map((faculty) => (
                    <MenuItem key={faculty.code} value={faculty.name_en}>
                      {faculty.name_en} ({faculty.name_th})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.graduationYear')} *`}
                  type="number"
                  fullWidth
                  required
                  value={formData.graduation_year}
                  onChange={(e) => setFormData({ ...formData, graduation_year: parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.gpa')}
                  type="number"
                  fullWidth
                  inputProps={{ step: 0.01, min: 0, max: 4 }}
                  value={formData.gpa}
                  onChange={(e) => setFormData({ ...formData, gpa: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('alumni.fields.currentOccupation')}
                  fullWidth
                  value={formData.current_occupation}
                  onChange={(e) => setFormData({ ...formData, current_occupation: e.target.value })}
                />
              </Grid>
            </Grid>
          )}

          {/* Tab 3: Contact & Address */}
          {tabValue === 3 && (
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.email')} *`}
                  type="email"
                  fullWidth
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.phone')} *`}
                  fullWidth
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.lineId')}
                  fullWidth
                  value={formData.line_id}
                  onChange={(e) => setFormData({ ...formData, line_id: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.facebookUrl')}
                  fullWidth
                  value={formData.facebook}
                  onChange={(e) => setFormData({ ...formData, facebook: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={`${t('alumni.fields.addressLine')} *`}
                  fullWidth
                  required
                  value={formData.address_line}
                  onChange={(e) => setFormData({ ...formData, address_line: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="จังหวัด (Province) *"
                  fullWidth
                  required
                  value={formData.province_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const province = provinces.find(p => p.code === parseInt(code));
                    setFormData({
                      ...formData,
                      province_code: code,
                      province: province ? getThaiName(province) : '',
                      district_code: '',
                      district: '',
                      sub_district_code: '',
                      sub_district: '',
                    });
                    if (code) fetchDistricts(parseInt(code), false);
                  }}
                  InputProps={{
                    endAdornment: geoLoading ? <CircularProgress size={20} /> : null,
                  }}
                >
                  <MenuItem value="">-- เลือกจังหวัด --</MenuItem>
                  {provinces.map((province) => (
                    <MenuItem key={province.code} value={province.code}>
                      {getThaiName(province)} ({province.en_name})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="อำเภอ/เขต (District) *"
                  fullWidth
                  required
                  value={formData.district_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const district = homeDistricts.find(d => d.code === parseInt(code));
                    setFormData({
                      ...formData,
                      district_code: code,
                      district: district ? getThaiName(district) : '',
                      sub_district_code: '',
                      sub_district: '',
                    });
                    if (code) fetchSubDistricts(parseInt(code), false);
                  }}
                  disabled={!formData.province_code || geoLoading}
                  InputProps={{
                    endAdornment: geoLoading ? <CircularProgress size={20} /> : null,
                  }}
                >
                  <MenuItem value="">-- เลือกอำเภอ/เขต --</MenuItem>
                  {homeDistricts.map((district) => (
                    <MenuItem key={district.code} value={district.code}>
                      {getThaiName(district)} ({district.en_name})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="ตำบล/แขวง (Sub-District) *"
                  fullWidth
                  required
                  value={formData.sub_district_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const subDistrict = homeSubDistricts.find(s => s.code === parseInt(code));
                    setFormData({
                      ...formData,
                      sub_district_code: code,
                      sub_district: subDistrict ? getThaiName(subDistrict) : '',
                      postal_code: subDistrict?.zipcode || formData.postal_code,
                    });
                  }}
                  disabled={!formData.district_code || geoLoading}
                  InputProps={{
                    endAdornment: geoLoading ? <CircularProgress size={20} /> : null,
                  }}
                >
                  <MenuItem value="">-- เลือกตำบล/แขวง --</MenuItem>
                  {homeSubDistricts.map((subDistrict) => (
                    <MenuItem key={subDistrict.code} value={subDistrict.code}>
                      {getThaiName(subDistrict)} ({subDistrict.en_name})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={`${t('alumni.fields.postalCode')} *`}
                  fullWidth
                  required
                  value={formData.postal_code}
                  onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                  {t('alumni.sections.workAddressInfo')}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('alumni.fields.workAddress')}
                  fullWidth
                  value={formData.work_address_line}
                  onChange={(e) => setFormData({ ...formData, work_address_line: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="จังหวัด (Province)"
                  fullWidth
                  value={formData.work_province_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const province = provinces.find(p => p.code === parseInt(code));
                    setFormData({
                      ...formData,
                      work_province_code: code,
                      work_province: province ? getThaiName(province) : '',
                      work_district_code: '',
                      work_district: '',
                      work_sub_district_code: '',
                      work_sub_district: '',
                    });
                    if (code) fetchDistricts(parseInt(code), true);
                  }}
                  InputProps={{
                    endAdornment: geoLoading ? <CircularProgress size={20} /> : null,
                  }}
                >
                  <MenuItem value="">-- เลือกจังหวัด --</MenuItem>
                  {provinces.map((province) => (
                    <MenuItem key={province.code} value={province.code}>
                      {getThaiName(province)} ({province.en_name})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="อำเภอ/เขต (District)"
                  fullWidth
                  value={formData.work_district_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const district = workDistricts.find(d => d.code === parseInt(code));
                    setFormData({
                      ...formData,
                      work_district_code: code,
                      work_district: district ? getThaiName(district) : '',
                      work_sub_district_code: '',
                      work_sub_district: '',
                    });
                    if (code) fetchSubDistricts(parseInt(code), true);
                  }}
                  disabled={!formData.work_province_code || geoLoading}
                  InputProps={{
                    endAdornment: geoLoading ? <CircularProgress size={20} /> : null,
                  }}
                >
                  <MenuItem value="">-- เลือกอำเภอ/เขต --</MenuItem>
                  {workDistricts.map((district) => (
                    <MenuItem key={district.code} value={district.code}>
                      {getThaiName(district)} ({district.en_name})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  select
                  label="ตำบล/แขวง (Sub-District)"
                  fullWidth
                  value={formData.work_sub_district_code}
                  onChange={(e) => {
                    const code = e.target.value;
                    const subDistrict = workSubDistricts.find(s => s.code === parseInt(code));
                    setFormData({
                      ...formData,
                      work_sub_district_code: code,
                      work_sub_district: subDistrict ? getThaiName(subDistrict) : '',
                      work_postal_code: subDistrict?.zipcode || formData.work_postal_code,
                    });
                  }}
                  disabled={!formData.work_district_code || geoLoading}
                  InputProps={{
                    endAdornment: geoLoading ? <CircularProgress size={20} /> : null,
                  }}
                >
                  <MenuItem value="">-- เลือกตำบล/แขวง --</MenuItem>
                  {workSubDistricts.map((subDistrict) => (
                    <MenuItem key={subDistrict.code} value={subDistrict.code}>
                      {getThaiName(subDistrict)} ({subDistrict.en_name})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label={t('alumni.fields.postalCode')}
                  fullWidth
                  value={formData.work_postal_code}
                  onChange={(e) => setFormData({ ...formData, work_postal_code: e.target.value })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('alumni.title')}</DialogTitle>
        <DialogContent>
          {selectedAlumni && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('alumni.fields.studentId')}</Typography>
                <Typography variant="body1">{selectedAlumni.student_id}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('common.profileInformation')}</Typography>
                <Typography variant="body1">
                  {selectedAlumni.first_name_en} {selectedAlumni.last_name_en}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAlumni.first_name_th} {selectedAlumni.last_name_th}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('alumni.education')}</Typography>
                <Typography variant="body1">
                  {selectedAlumni.education.degree}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedAlumni.education.faculty} - {selectedAlumni.education.graduation_year}
                </Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('alumni.contactAddress')}</Typography>
                <Typography variant="body2">{selectedAlumni.contact.email}</Typography>
                <Typography variant="body2">{selectedAlumni.contact.phone}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary">{t('common.status')}</Typography>
                <Chip
                  label={selectedAlumni.approval_status}
                  color={getStatusColor(selectedAlumni.approval_status) as any}
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t('alumni.delete')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('alumni.deleteConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>{t('common.cancel')}</Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog 
        open={photoDialogOpen} 
        onClose={() => {
          setPhotoDialogOpen(false);
          handleStopCamera();
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('common.uploadPhoto')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Camera View */}
            {cameraActive && (
              <Box
                sx={{
                  width: '100%',
                  bgcolor: 'black',
                  borderRadius: 1,
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 360,
                }}
              >
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  style={{ width: '100%', maxHeight: '480px' }}
                />
              </Box>
            )}

            <canvas ref={canvasRef} style={{ display: 'none' }} />

            {/* Upload Buttons */}
            {!cameraActive && (
              <Stack spacing={1}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                
                <Button
                  variant="contained"
                  startIcon={<UploadIcon />}
                  fullWidth
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {t('common.uploadPhoto')}
                </Button>

                <Button
                  variant="outlined"
                  startIcon={<PhotoCameraIcon />}
                  fullWidth
                  onClick={handleStartCamera}
                  disabled={uploading}
                >
                  {t('common.useCamera')}
                </Button>
              </Stack>
            )}

            {uploading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress />
              </Box>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          {cameraActive ? (
            <>
              <Button onClick={handleStopCamera}>{t('common.cancel')}</Button>
              <Button
                variant="contained"
                startIcon={<CameraAltIcon />}
                onClick={handleCapturePhoto}
                disabled={uploading}
              >
                {t('common.capturePhoto')}
              </Button>
            </>
          ) : (
            <Button onClick={() => setPhotoDialogOpen(false)}>{t('common.close')}</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AlumniManagement;
